import { useEffect } from "react";
import type * as React from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import MembershipExpiredBanner from "components/v3/sharedComponents/MembershipExpiredBanner";
import Translate from "translate/Translate";
import {
  getTellOthersEligibility,
  shouldShowTellOthersGenomicLifeCard
} from "components/v3/pages/account/PlanInformationPage/tellOthersGenomicLifeUtils";
import AccountMemberIcon from "assets/icons/account-management-member.svg";
import AccountPlanCardIcon from "assets/icons/account-management-plan-card.svg";
import AccountLockIcon from "assets/icons/account-management-lock.svg";
import AccountBillingIcon from "assets/icons/account-management-billing.png";
import AccountManagementMenuRow from "./AccountManagementMenuRow";
import AccountSecurityCallout from "./AccountSecurityCallout";

const menuRowIconImg = (src: string) => (
  <Box component="img" src={src} alt="" sx={{ display: "block" }} />
);

const AccountManagementPage: React.FC = () => {
  const userInformation = useSelector(selectUserPersonalInformation);
  const isMemberActive = userInformation.isMemberActive !== false;
  const canGiftOrInvite = shouldShowTellOthersGenomicLifeCard(
    getTellOthersEligibility(
      userInformation.eligibleToGift,
      userInformation.eligibleToInvite
    )
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  PageTitle("Account Management");

  const menuItems = [
    {
      id: "mem-info",
      label: "Member Information",
      icon: menuRowIconImg(AccountMemberIcon),
      to: PATHS.ACCOUNT_MANAGEMENT_MEMBER_INFORMATION
    },
    {
      id: "pay-info",
      label: "Plan Information",
      icon: menuRowIconImg(AccountPlanCardIcon),
      to: PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION,
      showIndicator: !isMemberActive
    },
    ...(canGiftOrInvite
      ? [
          {
            id: "billing-payments",
            label: "Billing & Payments",
            icon: menuRowIconImg(AccountBillingIcon),
            to: PATHS.ACCOUNT_MANAGEMENT_BILLING_AND_PAYMENTS
          }
        ]
      : []),
    {
      id: "pri-sec",
      label: "Privacy & Security",
      icon: menuRowIconImg(AccountLockIcon),
      to: PATHS.ACCOUNT_MANAGEMENT_PRIVACY_AND_SECURITY
    }
  ];

  return (
    <GradientFramedLayout showHeader={true}>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          width: "100%"
        }}
      >
        {!isMemberActive && (
          <Box
            sx={{
              boxSizing: "border-box",
              paddingTop: { xs: 2, md: 5 },
              paddingX: { xs: 2, md: 3 },
              width: "100%"
            }}
          >
            <Box
              sx={{
                marginX: "auto",
                maxWidth: 700,
                paddingX: { xs: 0, md: 2 },
                width: "100%"
              }}
            >
              <MembershipExpiredBanner />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            alignItems: "center",
            boxSizing: "border-box",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            gap: 3,
            marginX: "auto",
            maxWidth: 520,
            minWidth: 0,
            paddingBottom: "40px",
            paddingTop: isMemberActive ? { xs: 2, md: 3 } : { xs: 2, md: 3 },
            paddingX: { xs: 2, md: 3 },
            width: "100%"
          }}
        >
          <Typography
            variant="h4_primary"
            component="h4"
            sx={{
              fontWeight: 700,
              textAlign: "left",
              width: "100%",
              mb: "5px"
            }}
          >
            <Translate text="Account Management" />
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              width: "100%",
              mb: "5px"
            }}
          >
            {menuItems.map((item) => (
              <AccountManagementMenuRow
                key={item.id}
                id={item.id}
                label={item.label}
                icon={item.icon}
                to={item.to}
                showIndicator={item.showIndicator ?? false}
              />
            ))}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              marginTop: "auto",
              width: "100%"
            }}
          >
            <AccountSecurityCallout />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default AccountManagementPage;
